import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  ClearStudents,
  GetStudentsRequest,
} from '@myStore/users-view/users-view.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { UsersService } from '@services/users/users.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IAppState } from '../app.state';
import { Login } from '../auth/auth.actions';
import { DeleteNotificationFromState } from '../notifications/notifications.actions';
import {
  ConfirmUser,
  CreateUser,
  CreateUserFailure,
  CreateUserSuccess,
  DeleteUser,
  DeleteUserFailure,
  DeleteUserSuccess,
  EditUser,
  EditUserFailure,
  EditUserSuccess,
  EUserActions,
  GetUnconfirmedCount,
  GetUnconfirmedCountSuccess,
  GetUser,
  GetUserFailure,
  GetUserGroups,
  GetUserGroupsFailure,
  GetUserGroupsSuccess,
  GetUsers,
  GetUsersSuccess,
  GetUserSuccess,
  ImportUsers,
  ImportUsersFailure,
  ImportUsersSuccess,
  Register,
  RegisterSuccess,
  RequestFailure,
  StartRegisterLoading,
} from './user.actions';

@Injectable()
export class UserEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _usersService: UsersService,
    private _router: Router,
    private _toastr: ToastrService
  ) {}

  getUsers$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetUsers>(EUserActions.GetUsers),
        switchMap(action => {
          return this._usersService.getUsers(action.options?.qparams).pipe(
            map(users => {
              this._store.dispatch(new GetUsersSuccess(users));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При получении пользователей возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new RequestFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  getUnconfirmedCountUsers$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetUnconfirmedCount>(EUserActions.GetUnconfirmedCount),
        switchMap(() => {
          return this._usersService.getUnconfirmedCount().pipe(
            map(count => {
              this._store.dispatch(new GetUnconfirmedCountSuccess(count.count));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'Не удалось получить статистику по неподтвержденным пользователям'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  register$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<Register>(EUserActions.Register),
        map(action => action.user),
        switchMap(userData => {
          this._store.dispatch(new StartRegisterLoading());
          return this._usersService.register(userData).pipe(
            map(resUserData => {
              this._store.dispatch(new RegisterSuccess());
              this._store.dispatch(
                new Login({
                  email: userData.email,
                  password: userData.password,
                })
              );
              return resUserData;
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При регистрации возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new RequestFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  import$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<ImportUsers>(EUserActions.ImportUsers),
        switchMap(action => {
          return this._usersService.importUsers(action.data).pipe(
            map(res => {
              if (res.skipped) {
                this._store.dispatch(new ImportUsersSuccess(res.skipped));
              } else {
                this._store.dispatch(new ImportUsersSuccess());
              }
              this._store.dispatch(new ClearStudents());
              this._store.dispatch(
                new GetStudentsRequest(action.groupId, { status: 'active' })
              );
              this._toastr.success(res.detail, '', {
                timeOut: 3500,
              });
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При импорте возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new ImportUsersFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  confirmUser$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<ConfirmUser>(EUserActions.ConfirmUser),
        switchMap(action => {
          return this._usersService.confirm_user(action.id).pipe(
            map(res => {
              if (action.notificationId) {
                this._toastr.success(res.detail, '', { timeOut: 2000 });
                this._store.dispatch(
                  new DeleteNotificationFromState(action.notificationId)
                );
              }
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При подтверждении возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new RequestFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  getUser$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetUser>(EUserActions.GetUser),
        switchMap(action => {
          return this._usersService.getUser(action.id).pipe(
            map(res => {
              this._store.dispatch(new GetUserSuccess(res));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При получении пользователя возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new GetUserFailure());
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  getUserGroups$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetUserGroups>(EUserActions.GetUserGroups),
        switchMap(() => {
          return this._usersService.getGroups().pipe(
            map(res => {
              this._store.dispatch(new GetUserGroupsSuccess(res));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'Не удалось получить список ролей'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new GetUserGroupsFailure());
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  createUser$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<CreateUser>(EUserActions.CreateUser),
        switchMap(action => {
          return this._usersService.createUser(action.user).pipe(
            map(res => {
              this._store.dispatch(new CreateUserSuccess(res));
              this._router.navigate([`/users/${res.id}`]);
              this._toastr.success('Пользователь успешно создан');
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При создании пользователя возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new CreateUserFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  deleteUser$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DeleteUser>(EUserActions.DeleteUser),
        switchMap(action => {
          return this._usersService.deleteUser(action.id).pipe(
            map(() => {
              this._store.dispatch(new DeleteUserSuccess(action.id));

              if (action.options?.navTo) {
                this._router.navigate([action.options.navTo]);
              }

              this._toastr.success('Пользователь успешно удален');
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'Не удалось удалить пользователя'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new DeleteUserFailure());
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  editUser$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<EditUser>(EUserActions.EditUser),
        switchMap(action => {
          return this._usersService.editUser(action.id, action.userData).pipe(
            map(res => {
              this._store.dispatch(new EditUserSuccess(res.user));

              if (action.options?.message) {
                this._toastr.success(action.options?.message);
              } else {
                this._toastr.success(res.detail);
              }

              if (action.options?.navTo) {
                this._router.navigate([action.options?.navTo]);
              }
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При редактировании пользователя возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new EditUserFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );
}
